import { useState, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import {
  TrendingUp, Minus, ArrowDownLeft,
  Shield, Briefcase, Plus, MinusCircle,
} from 'lucide-react';
import type {
  ClaimFinancialSummary,
  ClaimTransaction,
  UpsertFinancialSummaryRequest,
  FeeType,
} from '@/api/generated/client/models';
import { deriveFinancials } from '@/hooks/use-claim-financials';
import { formatCentsSafe, dollarsToCents, centsToDollars } from '@/lib/utils';

// -- Editable field key types --

/** Summary fields stored as integer cents. */
type CentsField = Extract<keyof UpsertFinancialSummaryRequest,
  | 'rcvCents' | 'depreciationCents' | 'recoverableDepreciationCents'
  | 'nonRecoverableDepreciationCents' | 'deductibleCents' | 'acvCents'
  | 'netClaimCents' | 'netWithRecoverableCents' | 'flatFeeCents' | 'expectedFeeCents'
>;

/** Summary fields stored as a numeric percentage. */
type PctField = Extract<keyof UpsertFinancialSummaryRequest, 'feePercentage'>;

/** Union of all inline-editable field keys. */
type EditableField = CentsField | PctField;

// -- Props --

export interface SummaryTabProps {
  summary: ClaimFinancialSummary | null;
  transactions: ClaimTransaction[];
  onSave: (data: UpsertFinancialSummaryRequest) => void;
  canWrite: boolean;
}

// -- Component --

export function SummaryTab({
  summary,
  transactions,
  onSave,
  canWrite,
}: SummaryTabProps) {
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [draft, setDraft] = useState('');

  // All derived financial values — pure computation lives in the hook
  const {
    rcvCents,
    depreciationCents,
    recoverableCents,
    nonRecoverableCents,
    deductibleCents,
    acvCents,
    netClaimCents,
    netWithRecoverableCents,
    totalPaymentsCents: totalPaymentsInCents,
    totalFeesCollectedCents,
    expectedFeeCents,
  } = deriveFinancials(summary, transactions);

  // -- Field editing --
  const startEditCents = (field: CentsField, currentCents: number | null | undefined) => {
    if (!canWrite) return;
    setEditingField(field);
    setDraft(currentCents != null ? centsToDollars(currentCents) : '');
  };

  const startEditPct = (field: PctField, currentPct: number | null | undefined) => {
    if (!canWrite) return;
    setEditingField(field);
    setDraft(currentPct != null ? String(currentPct) : '');
  };

  const cancel = useCallback(() => setEditingField(null), []);

  const saveCentsField = useCallback((field: CentsField) => {
    if (!canWrite) return;
    if (draft === '') {
      // Empty string means clear the value
      onSave({ [field]: null });
      setEditingField(null);
      return;
    }
    
    const cents = dollarsToCents(draft);
    // Validate: dollarsToCents returns undefined for invalid input
    if (cents == null) {
      // Keep in edit mode if invalid - don't save undefined unintentionally
      return;
    }
    
    onSave({ [field]: cents });
    setEditingField(null);
  }, [draft, onSave, canWrite]);

  const savePctField = useCallback((field: PctField) => {
    if (!canWrite) return;
    if (draft === '') {
      // Empty string means clear the value
      onSave({ [field]: null });
      setEditingField(null);
      return;
    }
    
    const val = parseFloat(draft);
    // Validate: must be a valid number, not NaN
    if (isNaN(val)) {
      // Keep in edit mode if invalid - don't save NaN
      return;
    }
    
    onSave({ [field]: val });
    setEditingField(null);
  }, [draft, onSave, canWrite]);

  return (
    <div className="space-y-1.5">
      {/* Estimate Waterfall */}
      <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Estimate Breakdown</p>

      <div className="space-y-0.5">
        {/* RCV - editable */}
        <EditableEstimateRow
          icon={TrendingUp} label="RCV" valueCents={rcvCents}
          fieldKey="rcvCents" editingField={editingField}
          onStartEdit={() => startEditCents('rcvCents', summary?.rcvCents)}
          draft={draft} setDraft={setDraft}
          onSave={saveCentsField} onCancel={cancel} canWrite={canWrite}
        />
        {/* Depreciation - editable */}
        <EditableEstimateRow
          icon={Minus} iconColor="text-red-400" label="Depreciation" valueCents={depreciationCents} prefix={"\u2212"}
          fieldKey="depreciationCents" editingField={editingField}
          onStartEdit={() => startEditCents('depreciationCents', summary?.depreciationCents)}
          draft={draft} setDraft={setDraft}
          onSave={saveCentsField} onCancel={cancel} canWrite={canWrite}
        />
        {/* ACV - auto-computed, editable override */}
        <EditableHighlightRow
          label="ACV" valueCents={acvCents}
          fieldKey="acvCents" editingField={editingField}
          onStartEdit={() => startEditCents('acvCents', acvCents)}
          draft={draft} setDraft={setDraft}
          onSave={saveCentsField} onCancel={cancel} canWrite={canWrite}
        />
        {/* Deductible - editable */}
        <EditableEstimateRow
          icon={Shield} iconColor="text-orange-500" label="Deductible" valueCents={deductibleCents} prefix={"\u2212"}
          fieldKey="deductibleCents" editingField={editingField}
          onStartEdit={() => startEditCents('deductibleCents', summary?.deductibleCents)}
          draft={draft} setDraft={setDraft}
          onSave={saveCentsField} onCancel={cancel} canWrite={canWrite}
        />
        {/* Net Claim - auto-computed, editable override */}
        <EditableHighlightRow
          label="Net Claim" valueCents={netClaimCents}
          fieldKey="netClaimCents" editingField={editingField}
          onStartEdit={() => startEditCents('netClaimCents', netClaimCents)}
          draft={draft} setDraft={setDraft}
          onSave={saveCentsField} onCancel={cancel} canWrite={canWrite}
        />
        {/* Recoverable Dep - editable */}
        <EditableEstimateRow
          icon={Plus} iconColor="text-green-500" label="Recoverable Depr." valueCents={recoverableCents} prefix="+"
          fieldKey="recoverableDepreciationCents" editingField={editingField}
          onStartEdit={() => startEditCents('recoverableDepreciationCents', summary?.recoverableDepreciationCents)}
          draft={draft} setDraft={setDraft}
          onSave={saveCentsField} onCancel={cancel} canWrite={canWrite}
        />
        {/* Non-Recoverable Dep - editable */}
        <EditableEstimateRow
          icon={MinusCircle} iconColor="text-red-300" label="Non-Recoverable Depr." valueCents={nonRecoverableCents}
          fieldKey="nonRecoverableDepreciationCents" editingField={editingField}
          onStartEdit={() => startEditCents('nonRecoverableDepreciationCents', summary?.nonRecoverableDepreciationCents)}
          draft={draft} setDraft={setDraft}
          onSave={saveCentsField} onCancel={cancel} canWrite={canWrite}
        />
        {/* Net w/ Recoverable - auto-computed, editable override */}
        <EditableHighlightRow
          label="Net w/ Recoverable" valueCents={netWithRecoverableCents}
          fieldKey="netWithRecoverableCents" editingField={editingField}
          onStartEdit={() => startEditCents('netWithRecoverableCents', netWithRecoverableCents)}
          draft={draft} setDraft={setDraft}
          onSave={saveCentsField} onCancel={cancel} canWrite={canWrite}
          color="text-green-600"
        />
      </div>

      {/* Fees (inline-editable) */}
      <div className="border-t pt-1.5 mt-1.5">
        <div className="space-y-0.5">
          {/* Fee Type Radio Buttons */}
          <div className="flex items-center justify-between py-0.5 min-h-[26px]">
            <span className="text-xs text-muted-foreground">Fee Type</span>
            <RadioGroup 
              value={summary?.feeType || ''} 
              onValueChange={(value) => {
                if (!canWrite) return;
                onSave({ feeType: (value || undefined) as FeeType | undefined });
              }}
              disabled={!canWrite}
              className="flex gap-3"
            >
              <div className="flex items-center space-x-1">
                <RadioGroupItem value="percentage" id="fee-percentage" className="h-3 w-3" />
                <Label htmlFor="fee-percentage" className="text-[11px] font-normal cursor-pointer">Percentage</Label>
              </div>
              <div className="flex items-center space-x-1">
                <RadioGroupItem value="flat_fee" id="fee-flat" className="h-3 w-3" />
                <Label htmlFor="fee-flat" className="text-[11px] font-normal cursor-pointer">Flat Fee</Label>
              </div>
            </RadioGroup>
          </div>
          {summary?.feeType === 'percentage' && (
              <EditablePctRow label="Fee %" valuePct={summary?.feePercentage} fieldKey="feePercentage"
                editingField={editingField} onStartEdit={startEditPct} draft={draft} setDraft={setDraft}
                onSave={savePctField} onCancel={cancel} canWrite={canWrite} />
          )}
          {summary?.feeType === 'flat_fee' && (
            <EditableCentsRow label="Flat Fee" valueCents={summary?.flatFeeCents} fieldKey="flatFeeCents"
              editingField={editingField} onStartEdit={startEditCents} draft={draft} setDraft={setDraft}
              onSave={saveCentsField} onCancel={cancel} canWrite={canWrite} />
          )}
          {summary?.feeType === 'percentage' && (
            <EditableCentsRow label="Expected Fees" valueCents={expectedFeeCents} fieldKey="expectedFeeCents"
              editingField={editingField} onStartEdit={startEditCents} draft={draft} setDraft={setDraft}
              onSave={saveCentsField} onCancel={cancel} canWrite={canWrite} />
          )}
        </div>
      </div>

      {/* Collections */}
      <div className="border-t pt-1.5 mt-1.5">
        <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider mb-1">Collections</p>
        <div className="space-y-1">
          <SummaryRow icon={ArrowDownLeft} iconColor="text-green-600" label="Payments Collected" value={totalPaymentsInCents} />
          <SummaryRow icon={Briefcase} iconColor="text-blue-500" label="Fees Collected" value={totalFeesCollectedCents} />
        </div>
      </div>
    </div>
  );
}

// -- Summary Row (read-only with icon) --

function SummaryRow({ icon: Icon, iconColor, label, value, prefix }: {
  icon: React.ComponentType<{ className?: string }>;
  iconColor?: string;
  label: string;
  value: number | null | undefined;
  prefix?: string;
}) {
  return (
    <div className="flex items-center justify-between text-xs py-0.5 min-h-[26px]">
      <div className="flex items-center gap-1.5 text-muted-foreground">
        <Icon className={`h-3.5 w-3.5 ${iconColor || ''}`} />
        <span>{label}</span>
      </div>
      <span className="font-medium tabular-nums">
        {prefix && <span className="text-muted-foreground mr-0.5">{prefix}</span>}
        {formatCentsSafe(value)}
      </span>
    </div>
  );
}

// -- Editable Highlight Row (bold, inline-editable override for computed values) --

interface EditableHighlightRowProps {
  label: string;
  valueCents: number;
  color?: string;
  fieldKey: CentsField;
  editingField: EditableField | null;
  onStartEdit: () => void;
  draft: string;
  setDraft: (v: string) => void;
  onSave: (field: CentsField) => void;
  onCancel: () => void;
  canWrite: boolean;
}

function EditableHighlightRow({
  label, valueCents, color = 'text-foreground',
  fieldKey, editingField, onStartEdit, draft, setDraft, onSave, onCancel,
  canWrite,
}: EditableHighlightRowProps) {
  const isEditing = editingField === fieldKey;
  return (
    <div className="flex items-center justify-between py-0.5 min-h-[26px] text-xs font-semibold">
      <div className="flex items-center gap-1.5">
        {/* spacer matching the icon width in editable rows */}
        <span className="inline-block h-3.5 w-3.5 shrink-0" />
        <span>{label}</span>
      </div>
      {isEditing ? (
          <Input type="number" step="0.01" min="0" placeholder="$0.00" className="h-6 text-xs w-24 text-right font-semibold"
            value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus
            onBlur={() => onSave(fieldKey)}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') onCancel(); }} />
      ) : (
        <button
          onClick={onStartEdit}
          disabled={!canWrite}
          className={`tabular-nums text-right ${color} ${canWrite ? 'hover:text-primary transition-colors cursor-pointer' : 'cursor-default'}`}
        >
          {formatCentsSafe(valueCents)}
        </button>
      )}
    </div>
  );
}

// -- Editable Estimate Row (icon + inline edit) --

interface EditableEstimateRowProps {
  icon: React.ComponentType<{ className?: string }>;
  iconColor?: string;
  label: string;
  valueCents: number;
  prefix?: string;
  fieldKey: CentsField;
  editingField: EditableField | null;
  onStartEdit: () => void;
  draft: string;
  setDraft: (v: string) => void;
  onSave: (field: CentsField) => void;
  onCancel: () => void;
  canWrite: boolean;
}

function EditableEstimateRow({
  icon: Icon, iconColor, label, valueCents, prefix,
  fieldKey, editingField, onStartEdit, draft, setDraft, onSave, onCancel,
  canWrite,
}: EditableEstimateRowProps) {
  const isEditing = editingField === fieldKey;
  return (
    <div className="flex items-center justify-between py-0.5 min-h-[26px]">
      <div className="flex items-center gap-1.5 text-muted-foreground text-xs">
        <Icon className={`h-3.5 w-3.5 ${iconColor || ''}`} />
        <span>{label}</span>
      </div>
      {isEditing ? (
          <Input type="number" step="0.01" min="0" placeholder="$0.00" className="h-6 text-xs w-24 text-right"
            value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus
            onBlur={() => onSave(fieldKey)}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') onCancel(); }} />
      ) : (
        <button
          onClick={onStartEdit}
          disabled={!canWrite}
          className={`text-xs font-medium tabular-nums text-right ${canWrite ? 'hover:text-primary transition-colors cursor-pointer' : 'cursor-default'}`}
        >
          {prefix && <span className="text-muted-foreground mr-0.5">{prefix}</span>}
          {formatCentsSafe(valueCents)}
        </button>
      )}
    </div>
  );
}

// -- Editable Cents Row (no icon, for policy fields) --

interface EditableCentsRowProps {
  label: string;
  valueCents: number | null | undefined;
  fieldKey: CentsField;
  editingField: EditableField | null;
  onStartEdit: (field: CentsField, cents: number | null | undefined) => void;
  draft: string;
  setDraft: (v: string) => void;
  onSave: (field: CentsField) => void;
  onCancel: () => void;
  canWrite: boolean;
}

function EditableCentsRow({ label, valueCents, fieldKey, editingField, onStartEdit, draft, setDraft, onSave, onCancel, canWrite }: EditableCentsRowProps) {
  const isEditing = editingField === fieldKey;
  return (
    <div className="flex items-center justify-between py-0.5 min-h-[26px]">
      <span className="text-xs text-muted-foreground">{label}</span>
      {isEditing ? (
          <Input type="number" step="0.01" min="0" placeholder="$0.00" className="h-6 text-xs w-24 text-right"
            value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus
            onBlur={() => onSave(fieldKey)}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') onCancel(); }} />
      ) : (
        <button
          onClick={() => onStartEdit(fieldKey, valueCents)}
          disabled={!canWrite}
          className={`text-xs font-medium tabular-nums text-right ${canWrite ? 'hover:text-primary transition-colors cursor-pointer' : 'cursor-default'}`}
        >
          {valueCents != null ? formatCentsSafe(valueCents) : '\u2014'}
        </button>
      )}
    </div>
  );
}

// -- Editable Percentage Row --

interface EditablePctRowProps {
  label: string;
  valuePct: number | null | undefined;
  fieldKey: PctField;
  editingField: EditableField | null;
  onStartEdit: (field: PctField, pct: number | null | undefined) => void;
  draft: string;
  setDraft: (v: string) => void;
  onSave: (field: PctField) => void;
  onCancel: () => void;
  canWrite: boolean;
}

function EditablePctRow({ label, valuePct, fieldKey, editingField, onStartEdit, draft, setDraft, onSave, onCancel, canWrite }: EditablePctRowProps) {
  const isEditing = editingField === fieldKey;
  return (
    <div className="flex items-center justify-between py-0.5 min-h-[26px]">
      <span className="text-xs text-muted-foreground">{label}</span>
      {isEditing ? (
          <Input type="number" step="0.1" min="0" max="100" placeholder="0%" className="h-6 text-xs w-24 text-right"
            value={draft} onChange={(e) => setDraft(e.target.value)} autoFocus
            onBlur={() => onSave(fieldKey)}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); if (e.key === 'Escape') onCancel(); }} />
      ) : (
        <button
          onClick={() => onStartEdit(fieldKey, valuePct)}
          disabled={!canWrite}
          className={`text-xs font-medium tabular-nums text-right ${canWrite ? 'hover:text-primary transition-colors cursor-pointer' : 'cursor-default'}`}
        >
          {valuePct != null ? `${valuePct}%` : '\u2014'}
        </button>
      )}
    </div>
  );
}
